import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Skeleton,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { motion } from 'framer-motion';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import THEME from '../../../constants/theme';

const PhotoCarousel = ({
  photos = [],
  altText = 'Gallery Image',
  title = '',
  containerStyle = {},
  compact = false
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  // Responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // Responsive offsets: more subtle on mobile/tablet - memoized for performance
  const offsets = useMemo(() => {
    if (isMobile) {
      return [
        { x: -12, y: -10 },
        { x: 10, y: 14 },
        { x: -10, y: 12 },
        { x: 10, y: -14 },
        { x: -8, y: 10 },
        { x: 12, y: -8 },
        { x: -14, y: 16 }
      ];
    } else if (isTablet) {
      return [
        { x: -18, y: -12 },
        { x: 12, y: 22 },
        { x: -12, y: 16 },
        { x: 16, y: -18 },
        { x: -10, y: 11 },
        { x: 18, y: -10 },
        { x: -20, y: 24 }
      ];
    } else {
      return [
        { x: -50, y: -40 },
        { x: 45, y: 55 },
        { x: -40, y: 45 },
        { x: 50, y: -45 },
        { x: -35, y: 35 },
        { x: 55, y: -35 },
        { x: -45, y: 50 }
      ];
    }
  }, [isMobile, isTablet]);

  // Animation variants for photo transitions
  const photoVariants = {
    hidden: offset => ({
      x: offset.x,
      y: offset.y,
      scale: 0.8,
      opacity: 0.8,
      zIndex: 0
    }),
    visible: {
      x: 0,
      y: 0,
      scale: 1,
      opacity: 1,
      zIndex: 2,
      transition: { duration: 0.5, ease: 'easeOut' }
    },
    inactive: offset => ({
      x: offset.x,
      y: offset.y,
      scale: 0.9,
      opacity: 0.8,
      zIndex: 1,
      transition: { duration: 0.5, ease: 'easeIn' }
    })
  }; // Preload adjacent images (next, previous, and current + 2)
  useEffect(() => {
    const preloadImage = index => {
      if (photos[index]) {
        const img = new window.Image();
        img.src = photos[index];
        // Add loading priority for better performance
        img.loading = 'eager';
      }
    };

    // Preload current and adjacent images
    preloadImage(currentIndex);
    preloadImage((currentIndex + 1) % photos.length);
    preloadImage((currentIndex - 1 + photos.length) % photos.length);

    // Preload one more image ahead for smoother transitions
    if (photos.length > 3) {
      preloadImage((currentIndex + 2) % photos.length);
    }
  }, [currentIndex, photos]);

  const handleNext = () => {
    if (photos.length <= 1) return;
    setIsLoaded(false);
    setCurrentIndex(prevIndex => (prevIndex + 1) % photos.length);
  };

  const handlePrevious = () => {
    if (photos.length <= 1) return;
    setIsLoaded(false);
    setCurrentIndex(prevIndex =>
      prevIndex === 0 ? photos.length - 1 : prevIndex - 1
    );
  }; // Set image as loaded when it's ready with improved performance
  useEffect(() => {
    if (!photos[currentIndex]) return;

    setIsLoaded(false);
    const img = new window.Image();
    img.src = photos[currentIndex];
    img.loading = 'eager'; // Prioritize current image loading
    img.fetchPriority = 'high'; // Modern browsers optimization

    img.onload = () => {
      // Small delay to ensure smooth transition
      setTimeout(() => setIsLoaded(true), 100);
    };

    img.onerror = () => {
      // Failed to load image - using fallback
      setIsLoaded(true); // Still show the component even if image fails
    };

    // Cleanup
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [currentIndex, photos]);

  if (!photos || photos.length === 0) {
    return null;
  }

  // Responsive height values, make tablet almost as large as desktop
  const getResponsiveHeight = () => {
    if (compact) {
      if (isMobile) return '220px';
      if (isTablet) return '300px';
      return '340px';
    } else {
      if (isMobile) return '280px';
      if (isTablet) return '380px';
      return '440px';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: compact ? '1rem 0' : '3rem 0 8rem 0',
        position: 'relative',
        width: '100%',
        height: '100%',
        ...containerStyle
      }}
    >
      {title && (
        <Typography
          variant='h4'
          sx={{
            marginBottom: compact ? '1rem' : '2rem',
            color: THEME.COLORS.NEUTRAL_BLACK,
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
            textAlign: 'center'
          }}
        >
          {title}
        </Typography>
      )}

      {/* Photo Stack */}
      <Box
        sx={{
          width: compact ? '100%' : '90%',
          maxWidth: { xs: 360, sm: 500, md: 650 },
          height: getResponsiveHeight(),
          position: 'relative',
          borderRadius: '4px'
        }}
      >
        {photos.map((photo, index) => {
          const isActive = index === currentIndex;
          const offset = offsets[index % offsets.length];
          return (
            <motion.div
              key={index}
              custom={offset}
              variants={photoVariants}
              initial='hidden'
              animate={isActive ? 'visible' : 'inactive'}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none'
              }}
            >
              {!isLoaded && isActive ? (
                <Skeleton
                  variant='rectangular'
                  animation='wave'
                  width='100%'
                  height='100%'
                  sx={{
                    borderRadius: '4px',
                    backgroundColor: THEME.COLORS.NEUTRAL_GRAY_LIGHT
                  }}
                />
              ) : (
                <img
                  src={photo}
                  alt={`${altText} ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    userSelect: 'none',
                    imageRendering: 'crisp-edges',
                    imageOrientation: 'from-image'
                  }}
                  loading={isActive ? 'eager' : 'lazy'}
                  onLoad={() => setIsLoaded(true)}
                  onError={() => setIsLoaded(true)}
                />
              )}
            </motion.div>
          );
        })}

        {/* Navigation Buttons */}
        {photos.length > 1 && (
          <>
            <IconButton
              onClick={handlePrevious}
              size='large'
              aria-label='Previous image'
              sx={{
                position: 'absolute',
                top: '50%',
                left: { xs: 0, sm: 4, md: 0 },
                transform: 'translateY(-50%)',
                color: THEME.COLORS.NEUTRAL_WHITE,
                zIndex: 3,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  transform: 'translateY(-50%) scale(1.1)',
                  transition:
                    'transform 0.2s ease-out, background-color 0.2s ease-out'
                },
                '&:focus': {
                  backgroundColor: 'rgba(0, 0, 0, 0.5)'
                },
                '&:active': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  transform: 'translateY(-50%) scale(1.2)',
                  transition:
                    'transform 0.2s ease-out, background-color 0.2s ease-out'
                }
              }}
            >
              <KeyboardArrowLeftIcon
                sx={{
                  fontSize: compact ? { xs: 28, sm: 36 } : { xs: 36, sm: 44 }
                }}
              />
            </IconButton>
            <IconButton
              onClick={handleNext}
              size='large'
              aria-label='Next image'
              sx={{
                position: 'absolute',
                top: '50%',
                right: { xs: 0, sm: 4, md: 0 },
                transform: 'translateY(-50%)',
                color: THEME.COLORS.NEUTRAL_WHITE,
                zIndex: 3,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  transform: 'translateY(-50%) scale(1.1)',
                  transition:
                    'transform 0.2s ease-out, background-color 0.2s ease-out'
                },
                '&:focus': {
                  backgroundColor: 'rgba(0, 0, 0, 0.5)'
                },
                '&:active': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  transform: 'translateY(-50%) scale(1.2)',
                  transition:
                    'transform 0.2s ease-out, background-color 0.2s ease-out'
                }
              }}
            >
              <KeyboardArrowRightIcon
                sx={{
                  fontSize: compact ? { xs: 28, sm: 36 } : { xs: 36, sm: 44 }
                }}
              />
            </IconButton>
          </>
        )}

        {/* Photo Counter */}
        {photos.length > 1 && (
          <Typography
            variant='body2'
            sx={{
              position: 'absolute',
              bottom: {
                xs: '-2rem',
                sm: '-2rem',
                md: '-3.8rem', // Move it further down only on desktop
                lg: '-3.8rem'
              },
              left: '50%',
              transform: 'translateX(-50%)',
              color: THEME.COLORS.NEUTRAL_GRAY_DARK,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY
            }}
          >
            {currentIndex + 1} / {photos.length}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PhotoCarousel;
